import { cn, AppLogo, Card, Spinner, ChevronDownIcon } from '../../ui';
import type { Membership } from '../../lib/identity';

// Cold-start company chooser (ADMINISTRATION-DESIGN §9 [C1]). Rendered by CompanyProvider only
// when multiple active memberships are discovered — never auto-selects, even when one choice is
// preferred. `choice.isPreferred` (the last company the caller had active, 0047) still drives a
// quiet visual highlight — a primary outline + tinted monogram — so the likely choice stands out
// without a text label asserting anything. Presentational only: no arbitrary company-id input,
// only buttons bound to the discovered active choices. Reuses existing design-system primitives/
// tokens (Card/Spinner, semantic color tokens) for premium/dark-mode consistency with the rest of
// the app — no raw palette values, so a future Navy & Teal retheme flows through automatically.
export interface CompanyChooserProps {
  readonly choices: readonly Membership[];
  readonly selecting: boolean;
  readonly errorMessage: string | null;
  readonly onSelect: (companyId: string) => void;
}

export function CompanyChooser({ choices, selecting, errorMessage, onSelect }: CompanyChooserProps): JSX.Element {
  return (
    <div className="flex h-full w-full items-center justify-center overflow-auto bg-bg p-4 sm:p-8">
      <div className="w-full max-w-md">
        <div className="mb-6 flex items-center justify-center">
          <AppLogo />
        </div>

        <Card>
          {/* Visually removed (Product Owner option B — the logo above already says where you
              are, the cards already say what to do; a heading between them was redundant weight).
              Kept sr-only so the screen still exposes an accessible name/landmark for AT users. */}
          <h1 className="sr-only">აირჩიეთ კომპანია</h1>
          <p className="mb-4 text-center text-sm text-fg-muted">გააგრძელეთ სასურველი კომპანიის არჩევით</p>

          {errorMessage !== null && (
            <p
              role="alert"
              className="mb-4 rounded-token border border-danger bg-danger/10 p-3 text-sm text-danger"
            >
              {errorMessage}
            </p>
          )}

          <div className="flex flex-col gap-3" aria-busy={selecting}>
            {choices.map((choice) => (
              <button
                key={choice.companyId}
                type="button"
                disabled={selecting}
                onClick={() => onSelect(choice.companyId)}
                className={cn(
                  'flex min-h-[72px] w-full items-center gap-3 rounded-token bg-surface px-3 py-2 text-left transition-colors hover:bg-surface-muted',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-60',
                  choice.isPreferred ? 'border-2 border-primary' : 'border border-border',
                )}
              >
                <span
                  className={cn(
                    'flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold',
                    choice.isPreferred ? 'bg-primary/10 text-primary' : 'bg-surface-muted text-fg-muted',
                  )}
                  aria-hidden="true"
                >
                  {choice.companyName.charAt(0)}
                </span>
                <span className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                  <span className="font-medium text-fg">{choice.companyName}</span>
                </span>
                <ChevronDownIcon className="h-4 w-4 shrink-0 -rotate-90 text-fg-muted" />
              </button>
            ))}
          </div>

          {selecting && (
            <div className="mt-4 flex items-center justify-center gap-2 text-sm text-fg-muted">
              <Spinner size="sm" />
              <span>მიმდინარეობს არჩევა…</span>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
